import { useRef, type ReactNode } from 'react';
import { gsap, useGSAP, withMotion } from '../lib/motion.js';

interface RevealProps {
  children: ReactNode;
  /**
   * Stagger the wrapper's direct children in one after another instead of
   * lifting the whole block as a single object. What a grid wants.
   */
  stagger?: boolean;
  className?: string;
}

/**
 * Lifts its contents into place as they come into view, once.
 *
 * `gsap.from` rather than `fromTo`: the resting state is whatever the CSS
 * already says, so nothing here has to be kept in sync with the layout, and
 * a reader who prefers reduced motion simply never has the start state
 * applied — the content is visible from the first paint rather than hidden
 * by a class waiting for JavaScript to remove it.
 */
export function Reveal({ children, stagger = false, className }: RevealProps) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () =>
      withMotion(() => {
        const node = root.current;
        if (!node) return;

        gsap.from(stagger ? Array.from(node.children) : node, {
          y: 28,
          autoAlpha: 0,
          duration: 0.7,
          stagger: stagger ? 0.07 : 0,
          scrollTrigger: {
            trigger: node,
            // Fires a little before the block is fully on screen, so the
            // movement reads as the page settling rather than as a jolt.
            start: 'top 88%',
            once: true,
          },
        });
      }),
    { scope: root, dependencies: [stagger] },
  );

  return (
    <div ref={root} className={className}>
      {children}
    </div>
  );
}
